// What the « new version » bars say (components/QuickBuildProgress.tsx). Pure.
import type { QuickStatusResponse } from './api';

/** The in-tab fallback redraw's own progress (lib/quickPreview.ts QuickProgress). */
export interface LocalQuick {
  stage: string;
  done: number;
  total: number;
  ok?: boolean | null;
}

export interface BarModel {
  active: boolean;
  pct: number;
  /** French text, already filled in. */
  text: string;
  /** The same text as a template + values — what the admin translates. */
  tpl: string;
  vars: Record<string, string | number>;
  tone: 'run' | 'ok' | 'warn' | 'idle';
}

function bar(active: boolean, pct: number, tpl: string, tone: BarModel['tone'], vars: Record<string, string | number> = {}): BarModel {
  const text = tpl.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));
  return { active, pct, text, tpl, vars, tone };
}

function pctOf(done: number, total: number): number {
  return total > 0 ? Math.min(98, Math.round(5 + (done / total) * 90)) : 5;
}

export function quickBarModel(
  st: QuickStatusResponse | null,
  o: { album: number | null; local: LocalQuick | null; since: number; nowMs: number },
): { quick: BarModel; print: BarModel; settled: boolean } {
  // ── Aperçu 3D ──
  let quick: BarModel;
  if (o.local) {
    const l = o.local;
    if (l.stage === 'done') quick = l.ok === false
      ? bar(false, 0, 'arrivera avec le PDF', 'idle')
      : bar(false, 100, 'à jour ✓', 'ok');
    else quick = (l.total ? bar(true, pctOf(l.done, l.total), '{done}/{total} pages', 'run', { done: l.done, total: l.total }) : bar(true, 5, 'préparation…', 'run'));
  } else {
    const jobs = (st?.quick ?? [])
      .filter((j) => o.album == null || j.album === o.album)
      .filter((j) => !o.since || Date.parse(j.requestedAt) >= o.since - 5000);
    if (jobs.length === 0) {
      quick = st && !st.runner
        ? bar(false, 0, 'arrivera avec le PDF', 'idle')
        : bar(!!o.since && o.nowMs - o.since < 15_000, 2, 'en attente…', 'run');
    } else {
      const running = jobs.filter((j) => j.state === 'queued' || j.state === 'running');
      const failed = jobs.some((j) => j.state === 'failed' || j.state === 'expired');
      if (running.length) {
        const done = running.reduce((a, j) => a + j.done, 0);
        const total = running.reduce((a, j) => a + j.total, 0);
        const pct = Math.round(running.reduce((a, j) => a + j.pct, 0) / running.length);
        const stage = running[0].stage;
        quick = (running[0].state === 'queued' ? bar(true, pct, 'démarrage…', 'run')
          : stage === 'cover' ? bar(true, pct, 'couverture…', 'run')
            : total ? bar(true, pct, '{done}/{total} pages', 'run', { done, total }) : bar(true, pct, 'préparation…', 'run'));
      } else if (jobs.every((j) => j.state === 'done')) {
        const ms = Math.max(...jobs.map((j) => j.ms ?? 0));
        quick = (ms ? bar(false, 100, 'à jour ✓ ({s} s)', 'ok', { s: (ms / 1000).toFixed(1) }) : bar(false, 100, 'à jour ✓', 'ok'));
      } else if (failed) {
        quick = bar(false, 0, 'arrivera avec le PDF', 'warn');
      } else {
        // skipped: nothing to redraw (no book yet, or the print landed first).
        quick = bar(false, 100, 'avec le PDF', 'idle');
      }
    }
  }
  // ── PDF d'impression ──
  const p = st?.print ?? null;
  let print: BarModel;
  if (!p) print = bar(false, 0, '…', 'idle');
  else if (p.pdf_status === 'generating') {
    const started = Date.parse(String(p.pdf_render_started_at ?? ''));
    // A print takes 1–3 min: a time-based estimate, honest about being one.
    const s = Number.isFinite(started) ? Math.max(0, (o.nowMs - started) / 1000) : 0;
    print = (Number.isFinite(started) ? bar(true, Math.min(95, Math.round((s / 150) * 100)), 'en cours ({s} s)', 'run', { s: Math.round(s) }) : bar(true, 2, 'en file d’attente', 'run'));
  } else if (p.pdf_status === 'failed') print = bar(false, 0, 'échec — voir la fiche', 'warn');
  else if (p.pdf_status === 'ready') print = bar(false, 100, 'prêt ✓', 'ok');
  else print = bar(false, 0, '—', 'idle');
  return { quick, print, settled: !quick.active && !print.active && !!st };
}
